import { AfterViewInit, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { SvgIconComponentFactory } from 'projects/shared/svg-icons/svg-icon-component-factory';
import { SvgIconHostDirective } from 'projects/shared/svg-icons/svg-icon-host.directive';
import { AssetItem } from '../../../models/asset-item-model';
import { AssetType } from '../../../models/enums/asset-type-enum';
import { EnumsService } from '../../../services/enums.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'asset-item',
    templateUrl: 'asset-item.component.html',
    styleUrls: ['asset-item.component.scss'],
    standalone: false
})

export class AssetItemComponent implements AfterViewInit {

  private readonly _transloco = inject(TranslocoService);

  // static: false — the host directive lives inside a `*transloco` structural
  // directive, so a static query can't resolve it at ngOnInit time. Wait for
  // view init (ngAfterViewInit) to load the icon.
  @ViewChild(SvgIconHostDirective, { static: false }) svgIconHost?: SvgIconHostDirective;

  @Input()
  asset!: AssetItem;

  @Input()
  insideGroup: boolean = false;

  @Output() deleteClicked = new EventEmitter<AssetItem>();
  @Output() editClicked = new EventEmitter<AssetItem>();

  public get isReadOnly(): boolean {
    return this.asset.isReadOnly;
  }

  constructor(private readonly _svgIconFactory: SvgIconComponentFactory,
    private readonly _enumsService: EnumsService) {
  }

  public get assetTypeName(): string {
    return this._enumsService.assetTypeName(this.asset.type!);
  }

  public get assetTypeLabel(): string {
    if (!this.insideGroup && this.asset.depository) {
      return `${this.asset.depository} / ${this.assetTypeName}`;
    }
    return this.assetTypeName;
  }

  private get iconName(): string {
    switch (this.asset?.type) {
      case AssetType.CheckingAccount:
      case AssetType.SavingsAccount:
      case AssetType.MoneyMarketFund:
      case AssetType.CertificateOfDepositTimeDeposit:
        return 'stockholm-bank';
      case AssetType.Stock:
      case AssetType.StockOptions:
      case AssetType.MutualFund:
      case AssetType.Bond:
        return 'duotone-star';
      case AssetType.TrustAccount:
      case AssetType.RetirementFund:
      case AssetType.Annuity:
      case AssetType.LifeInsurance:
        return 'duotone-safe';
      case AssetType.Cash:
      case AssetType.EarnestMoneyCashDepositTowardPurchase:
      case AssetType.RelocationMoney:
        return 'duotone-wallet';
      case AssetType.CashGift:
      case AssetType.GiftOfEquity:
      case AssetType.Grant:
        return 'duotone-coins';
      case AssetType.Automobile:
        return 'duotone-tools';
      case AssetType.PendingNetSaleProceedsFromRealEstateAssets:
      case AssetType.ProceedsFromSaleOfNonRealEstateAsset:
        return 'duotone-home';
      default:
        return 'duotone-wallet';
    }
  }

  onEditClicked = () => {
    if (this.isReadOnly) {
      return;
    }
    this.editClicked.emit(this.asset);
  }

  onDeleteClicked = (e: any) => {
    e.cancelBubble = true;
    if(e.stopPropagation) e.stopPropagation();

    Swal.fire({
      title: this._transloco.translate('common.areYouSure'),
      text: this._transloco.translate('wizard.assets.confirmDeleteAsset', { assetType: this.assetTypeName }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this._transloco.translate('common.yesDeleteIt')
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteClicked.emit(this.asset);
      }
    });
  }

  private loadIcon = () => {
    const iconName = this.iconName;
    if (!iconName || !this.svgIconHost) {
      return;
    }
    const svgIconComponent = this._svgIconFactory.getSvIconComponent(iconName);
    if (!svgIconComponent) {
      return;
    }
    const viewContainerRef = this.svgIconHost.viewContainerRef;
    viewContainerRef.clear();
    viewContainerRef.createComponent(svgIconComponent);
  }

  ngAfterViewInit() {
    this.loadIcon();
  }
}
